import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Crown, Star, Music, Gamepad2, Ticket, Check, Loader2 } from 'lucide-react';
import { safeFetch } from '../utils/safeFetch';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';
const API = API_URL;

export function PremiumUpgradeCard({ clubId, clubName, currentTier = 'free' }) {
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [loading, setLoading] = useState(false);

  const plans = {
    monthly: {
      price: 5,
      interval: 'month',
      label: 'Monthly',
      savings: null
    },
    yearly: {
      price: 50,
      interval: 'year',
      label: 'Yearly',
      savings: '2 months free!'
    }
  };

  const features = [
    { icon: Star, name: 'Featured Club', description: 'Stand out in search results' },
    { icon: Music, name: 'DJ Live', description: 'Live dance request system' },
    { icon: Gamepad2, name: 'Bingo & Raffle', description: 'Interactive games for events' },
    { icon: Ticket, name: 'Ticket Selling', description: 'Sell tickets with integrated payments' }
  ];

  const handleUpgrade = async () => {
    setLoading(true);
    
    try {
      const res = await safeFetch(`${API}/api/stripe/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          club_id: clubId,
          plan: selectedPlan,
          origin_url: window.location.origin
        })
      });
      
      if (!res.ok) {
        const error = res.data;
        throw new Error(error.detail || 'Failed to create checkout session');
      }
      
      // Redirect to Stripe Checkout
      window.location.href = res.data.checkout_url;
      
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error(error.message || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  if (currentTier === 'premium') {
    return null; // Don't show if already premium
  }

  return (
    <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl flex items-center gap-2">
            <Crown className="w-6 h-6 text-amber-500" />
            Upgrade to Premium
          </CardTitle>
          <Badge className="bg-amber-500">Save with Yearly</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Plan Selection */}
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(plans).map(([key, plan]) => (
            <button
              key={key}
              onClick={() => setSelectedPlan(key)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                selectedPlan === key 
                  ? 'border-amber-500 bg-white shadow-md' 
                  : 'border-slate-200 bg-white/50 hover:border-amber-300'
              }`}
            >
              <div className="font-semibold text-slate-900">{plan.label}</div>
              <div className="text-2xl font-bold text-amber-600">
                £{plan.price}
                <span className="text-sm font-normal text-slate-500">/{plan.interval}</span>
              </div>
              {plan.savings && (
                <div className="text-xs text-green-600 font-medium mt-1">{plan.savings}</div>
              )}
            </button>
          ))}
        </div>

        {/* Features */}
        <div className="space-y-3">
          <h4 className="font-medium text-slate-900">Premium Features:</h4>
          <div className="grid grid-cols-2 gap-2">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-2 p-2 bg-white/80 rounded-lg">
                <div className="p-1.5 bg-amber-100 rounded">
                  <feature.icon className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-900">{feature.name}</div>
                  <div className="text-xs text-slate-500">{feature.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <Button 
          className="w-full bg-amber-500 hover:bg-amber-600 h-12 text-lg"
          onClick={handleUpgrade}
          disabled={loading}
          data-testid="upgrade-premium-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Redirecting to checkout...
            </>
          ) : (
            <>
              <Crown className="w-5 h-5 mr-2" />
              Upgrade for £{plans[selectedPlan].price}/{plans[selectedPlan].interval}
            </>
          )}
        </Button>
        
        <p className="text-xs text-center text-slate-500">
          Secure payment via Stripe. Cancel anytime.
        </p>
      </CardContent>
    </Card>
  );
}
